import { useState } from "react";
import { Box } from "@mui/material";
import { DotLegalButton, DotLegalCheckbox, DotLegalFullScreenSpinner, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { getSettings } from "@common/settingsProvider";
import PolicyPdfContent from "../policyPdf/PolicyPdfContent";
import PublicPageContainer from "@common/components/publicPageContainer/PublicPageContainer";
import { usePublicPolicyAcknowledgement } from "./PublicPolicyAcknowledgement.hooks";
import { PublicPolicyAcknowledgementState } from "./PublicPolicyAcknowledgement.types";

function PublicPolicyAcknowledgement() {
    const { translateString, translateDate } = useTranslation();
    const { data, isLoading, acknowledge, isAcknowledging, hasAcknowledged } = usePublicPolicyAcknowledgement();
    const [hasConfirmed, setHasConfirmed] = useState(false);

    if (isLoading || !data) {
        return <DotLegalFullScreenSpinner />;
    }

    const renderMessage = (message: string, header?: string) => (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
            {header && (
                <DotLegalHeader center headerStyle="xxxl">
                    {header}
                </DotLegalHeader>
            )}
            <DotLegalHeader headerStyle={"medium"} fontWeight={400} marginBottom={0}>
                {message}
            </DotLegalHeader>
        </Box>
    );

    if (hasAcknowledged) {
        return (
            <PublicPageContainer centerVertically>
                {renderMessage(translateString("policyAcknowledgementSuccess"), translateString("policyAcknowledgementSuccessHeader"))}
            </PublicPageContainer>
        );
    }

    if (data.state === PublicPolicyAcknowledgementState.NotFound) {
        return <PublicPageContainer centerVertically>{renderMessage(translateString("policyAcknowledgementNotFound"))}</PublicPageContainer>;
    }

    if (data.state === PublicPolicyAcknowledgementState.AlreadyAcknowledged) {
        const acknowledgedDate = data.acknowledgedAt ? translateDate(new Date(data.acknowledgedAt)) : "";
        return (
            <PublicPageContainer centerVertically>
                {renderMessage(translateString("policyAlreadyAcknowledged", { date: acknowledgedDate }))}
            </PublicPageContainer>
        );
    }

    return (
        <PublicPageContainer>
            {data.emailLogoBlobId && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Box
                        component="img"
                        src={`${getSettings().apiBaseUrl}/CustomerAssets/EmailLogo/${data.emailLogoBlobId}`}
                        alt=""
                        sx={{ maxWidth: 250, maxHeight: 60 }}
                    />
                </Box>
            )}
            {data.policyData && <PolicyPdfContent data={data.policyData} />}
            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <DotLegalCheckbox checked={hasConfirmed} onChange={setHasConfirmed} label={translateString("policyAcknowledgementConfirmCheckbox")} />
                <DotLegalButton buttonType="primary" disabled={!hasConfirmed} isLoading={isAcknowledging} onClick={() => acknowledge()}>
                    {translateString("acknowledgePolicy")}
                </DotLegalButton>
            </Box>
        </PublicPageContainer>
    );
}

export default PublicPolicyAcknowledgement;
